import { createProductAnalyticsEvent } from "./eventFactory";
import type { LatencyBucket, ProductAnalyticsEvent, ReasonCategory } from "./eventTypes";

type Clock = { now(): number };
type Options = { artifactSha: string; clock?: Clock };

export function latencyBucket(milliseconds: number): LatencyBucket {
  if (milliseconds < 250) return "lt_250ms";
  if (milliseconds < 1_000) return "250ms_1s";
  if (milliseconds < 3_000) return "1s_3s";
  if (milliseconds < 10_000) return "3s_10s";
  return "gte_10s";
}

export class NavigationMeasurement {
  private readonly clock: Clock;
  private readonly starts = new Map<string, number>();

  constructor(private readonly emit: (event: ProductAnalyticsEvent) => void, private readonly options: Options) {
    this.clock = options.clock ?? performance;
  }

  started(routeTemplate: string, eligibilityCategory: "eligible" | "redirect" | "access_denied" | "bot" | "synthetic_monitor" = "eligible") {
    const navigationId = crypto.randomUUID();
    this.starts.set(navigationId, this.clock.now());
    this.emit(createProductAnalyticsEvent("product_navigation_started", { navigationId, routeTemplate, eligibilityCategory }, {
      artifactSha: this.options.artifactSha,
    }));
    return navigationId;
  }

  ready(navigationId: string, routeTemplate: string) {
    const startedAt = this.starts.get(navigationId);
    if (startedAt === undefined) return false;
    this.starts.delete(navigationId);
    this.emit(createProductAnalyticsEvent("product_navigation_ready", {
      navigationId, routeTemplate, latencyBucket: latencyBucket(Math.max(0, this.clock.now() - startedAt)),
    }, { artifactSha: this.options.artifactSha }));
    return true;
  }

  failed(navigationId: string, routeTemplate: string, reasonCategory: ReasonCategory) {
    if (!this.starts.delete(navigationId)) return false;
    this.emit(createProductAnalyticsEvent("product_route_error", { navigationId, routeTemplate, reasonCategory }, {
      artifactSha: this.options.artifactSha,
    }));
    return true;
  }
}
